import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Users, Plus, Search, Phone, Mail, MapPin, 
  AlertCircle, Edit2, Trash2, User, Building2,
  PhoneCall, X, RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const positionColors = {
  "Partner": "bg-purple-100 text-purple-800 border-purple-200",
  "Office Manager": "bg-blue-100 text-blue-800 border-blue-200",
  "Front Office": "bg-green-100 text-green-800 border-green-200",
  "Staff": "bg-slate-100 text-slate-700 border-slate-200",
};

export default function ContactDirectory() {
  const { user } = useAuth();
  // Admin or partner (tracks_billings) can manage contacts
  const isAdmin = user?.role === "admin" || user?.tracks_billings === true;
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPosition, setFilterPosition] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    position: "",
    email: "",
    cell_phone: "",
    direct_line: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    emergency_contact_name: "",
    emergency_contact_relation: "",
    emergency_contact_phone: "",
    notes: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
        
        // If no contacts, try to seed them
        if (data.length === 0 && isAdmin) {
          await seedContacts();
        }
      }
    } catch (error) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const seedContacts = async (force = false) => {
    try {
      toast.info(force ? "Re-importing contacts..." : "Importing contacts...");
      console.log("Calling seed API...", { force });
      const response = await fetch(`${API_URL}/api/contacts/seed?force=${force}`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      console.log("Seed response status:", response.status);
      const data = await response.json();
      console.log("Seed response data:", data);
      
      if (response.ok) {
        toast.success(data.message);
        loadContacts();
      } else {
        toast.error(data.detail || data.message || "Failed to import contacts");
      }
    } catch (error) {
      console.error("Failed to seed contacts:", error);
      toast.error(`Failed to import contacts: ${error.message}`);
    }
  };

  const openDetailDialog = (contact) => {
    setSelectedContact(contact);
    setShowDetailDialog(true);
  };

  const openEditDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        position: contact.position || "",
        email: contact.email || "",
        cell_phone: contact.cell_phone || "",
        direct_line: contact.direct_line || "",
        address: contact.address || "",
        city: contact.city || "",
        state: contact.state || "",
        zip_code: contact.zip_code || "",
        emergency_contact_name: contact.emergency_contact_name || "",
        emergency_contact_relation: contact.emergency_contact_relation || "",
        emergency_contact_phone: contact.emergency_contact_phone || "",
        notes: contact.notes || ""
      });
    } else {
      setEditingContact(null);
      setFormData({
        first_name: "", last_name: "", position: "", email: "",
        cell_phone: "", direct_line: "", address: "", city: "",
        state: "", zip_code: "", emergency_contact_name: "",
        emergency_contact_relation: "", emergency_contact_phone: "", notes: ""
      });
    }
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error("First and last name are required");
      return;
    }

    try {
      const url = editingContact 
        ? `${API_URL}/api/contacts/${editingContact.id}`
        : `${API_URL}/api/contacts`;
      
      const response = await fetch(url, {
        method: editingContact ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingContact ? "Contact updated" : "Contact added");
        setShowEditDialog(false);
        loadContacts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save contact");
      }
    } catch (error) {
      toast.error("Failed to save contact");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;

    try {
      const response = await fetch(`${API_URL}/api/contacts/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success("Contact deleted");
        setShowDetailDialog(false);
        loadContacts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete contact");
      }
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  // Get unique positions for filter
  const positions = [...new Set(contacts.map(c => c.position).filter(Boolean))];

  // Filter contacts
  const filteredContacts = contacts.filter(c => {
    const matchesSearch = 
      c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cell_phone?.includes(searchTerm) ||
      c.direct_line?.includes(searchTerm);
    
    const matchesPosition = filterPosition === "all" || c.position === filterPosition;
    
    return matchesSearch && matchesPosition;
  });

  // Group contacts by position for display
  const groupedContacts = {
    "Partner": filteredContacts.filter(c => c.position === "Partner"),
    "Office Manager": filteredContacts.filter(c => c.position === "Office Manager"),
    "Front Office": filteredContacts.filter(c => c.position === "Front Office"),
    "Staff": filteredContacts.filter(c => c.position === "Staff" || !c.position),
  };

  return (
    <div className="space-y-6" data-testid="contact-directory">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Firm Contact Directory
          </h1>
          <p className="text-muted-foreground mt-1">
            {contacts.length} team members
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button 
              onClick={() => seedContacts(true)} 
              variant="outline"
              data-testid="reimport-contacts-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Re-Import Contacts
            </Button>
            <Button onClick={() => openEditDialog()} data-testid="add-contact-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </div>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-contacts"
          />
        </div>
        <Select value={filterPosition} onValueChange={setFilterPosition}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Positions</SelectItem>
            {positions.map(pos => (
              <SelectItem key={pos} value={pos}>{pos}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Contact Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading contacts...</div>
      ) : filteredContacts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              {searchTerm || filterPosition !== "all" 
                ? "No contacts match your search" 
                : "No contacts in the directory"}
            </p>
            {isAdmin && !searchTerm && filterPosition === "all" && (
              <Button onClick={seedContacts} variant="outline" className="mt-4">
                Import Contacts
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedContacts).map(([position, posContacts]) => {
            if (posContacts.length === 0) return null;
            return (
              <div key={position}>
                <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Badge className={positionColors[position] || positionColors["Staff"]}>
                    {position}
                  </Badge>
                  <span className="text-sm font-normal text-muted-foreground">
                    ({posContacts.length})
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {posContacts.map(contact => (
                    <Card 
                      key={contact.id} 
                      className="cursor-pointer hover:shadow-md transition-shadow border-l-4"
                      style={{ borderLeftColor: position === "Partner" ? "#9333ea" : position === "Office Manager" ? "#2563eb" : position === "Front Office" ? "#16a34a" : "#64748b" }}
                      onClick={() => openDetailDialog(contact)}
                      data-testid={`contact-card-${contact.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground truncate">
                              {contact.first_name} {contact.last_name}
                            </h3>
                            {contact.email && (
                              <a 
                                href={`mailto:${contact.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm text-primary hover:underline truncate block"
                              >
                                {contact.email}
                              </a>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                          {contact.cell_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5" />
                              <a 
                                href={`tel:${contact.cell_phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-primary"
                              >
                                {contact.cell_phone}
                              </a>
                            </div>
                          )}
                          {contact.direct_line && (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3.5 w-3.5" />
                              <span>{contact.direct_line}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Contact Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="text-xl">{selectedContact.first_name} {selectedContact.last_name}</div>
                    {selectedContact.position && (
                      <Badge className={`${positionColors[selectedContact.position] || positionColors["Staff"]} mt-1`}>
                        {selectedContact.position}
                      </Badge>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Contact Info */}
                <div className="space-y-3">
                  {selectedContact.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-slate-400" />
                      <a href={`mailto:${selectedContact.email}`} className="text-cyan-600 hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>
                  )}
                  {selectedContact.cell_phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-slate-400" />
                      <a href={`tel:${selectedContact.cell_phone}`} className="hover:text-cyan-600">
                        {selectedContact.cell_phone}
                        <span className="text-slate-400 text-sm ml-2">(Cell)</span>
                      </a>
                    </div>
                  )}
                  {selectedContact.direct_line && (
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-slate-400" />
                      <a href={`tel:${selectedContact.direct_line}`} className="hover:text-cyan-600">
                        {selectedContact.direct_line}
                        <span className="text-slate-400 text-sm ml-2">(Direct Line)</span>
                      </a>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {selectedContact.notes && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-500 mb-1">Notes</div>
                    <div className="text-sm">{selectedContact.notes}</div>
                  </div>
                )}
              </div>

              {isAdmin && (
                <DialogFooter className="gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => handleDelete(selectedContact.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                  <Button onClick={() => {
                    setShowDetailDialog(false);
                    openEditDialog(selectedContact);
                  }}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? "Edit Contact" : "Add New Contact"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="position">Position</Label>
              <Select 
                value={formData.position} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, position: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Partner">Partner</SelectItem>
                  <SelectItem value="Office Manager">Office Manager</SelectItem>
                  <SelectItem value="Front Office">Front Office</SelectItem>
                  <SelectItem value="Staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="cell_phone">Cell Phone</Label>
              <Input
                id="cell_phone"
                value={formData.cell_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, cell_phone: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="direct_line">Direct Line</Label>
              <Input
                id="direct_line"
                value={formData.direct_line}
                onChange={(e) => setFormData(prev => ({ ...prev, direct_line: e.target.value }))}
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="zip_code">ZIP</Label>
                <Input
                  id="zip_code"
                  value={formData.zip_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>

            <div className="col-span-2 border-t pt-4 mt-2">
              <h3 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Emergency Contact
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="emergency_name">Name</Label>
                  <Input
                    id="emergency_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_relation">Relationship</Label>
                  <Input
                    id="emergency_relation"
                    placeholder="Spouse, Parent, etc."
                    value={formData.emergency_contact_relation}
                    onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_relation: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_phone">Phone</Label>
                  <Input
                    id="emergency_phone"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingContact ? "Update Contact" : "Add Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
